import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  StyledModal,
  StyledModalHeader,
} from '../../../../../common/form/FormElements.jsx';
import { Spinner } from '../../../../../common/index.jsx';
import { CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';
import {
  useGetQuizQuestionQuery,
  useGetQuizQuestionCorrectAnswersQuery,
} from '../../../../../../redux/services/quiz_questions.jsx';
import { Grid } from 'antd';

const QuizQuestionPreviewModal = ({ open, questionId, onClose }) => {
  const { t } = useTranslation();

  // Guard: if no questionId provided but modal is supposed to be open, show info and avoid requests
  if (open && !questionId) {
    return (
      <StyledModal
        open={!!open}
        onCancel={onClose}
        onOk={onClose}
        okText={t('Zavrieť')}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <StyledModalHeader>
          <a className={`second-title title`} href="#">
            {t('Náhľad kvízovej otázky')}
          </a>
        </StyledModalHeader>
        <div style={{ color: '#ff4d4f', fontWeight: 600, padding: '8px 0' }}>
          {t('Otázka neexistuje alebo nebola zadaná.')}
        </div>
      </StyledModal>
    );
  }

  const { data, isLoading } = useGetQuizQuestionQuery(questionId, {
    skip: !questionId,
  });
  const { data: correctData, isLoading: isCorrectLoading } =
    useGetQuizQuestionCorrectAnswersQuery(
      { id: questionId },
      { skip: !questionId },
    );
  const screens = Grid.useBreakpoint();
  const previewQuestion =
    Array.isArray(data?.answers) || data?.text ? data : null;

  const correctIds = React.useMemo(
    () => new Set(correctData?.correct_answer_ids || []),
    [correctData],
  );

  return (
    <StyledModal
      open={!!open}
      onCancel={onClose}
      onOk={onClose}
      okText={t('Zavrieť')}
      cancelButtonProps={{ style: { display: 'none' } }}
      width={screens.md ? '60%' : '100%'}
    >
      <StyledModalHeader>
        <a className={`second-title title`} href="#">
          {t('Náhľad kvízovej otázky')}
        </a>
      </StyledModalHeader>

      {(isLoading || isCorrectLoading) && <Spinner />}
      {!isLoading && !isCorrectLoading && previewQuestion && (
        <div style={{ fontSize: '150%' }}>
          <span
            style={{
              display: 'inline-block',
              marginBottom: 8,
              fontWeight: 600,
              paddingRight: '10px',
            }}
          >
            {t('Kvízová otázka:')}
          </span>
          <div style={{ display: 'inline-block', marginBottom: 8 }}>
            {previewQuestion.text}
          </div>

          <div style={{ marginTop: 20, marginBottom: 12, fontWeight: 600 }}>
            {t('Odpovede')}:
          </div>
          <div>
            {previewQuestion.answers && previewQuestion.answers.length > 0 ? (
              [...previewQuestion.answers]
                .sort((a, b) => (a.position || 0) - (b.position || 0))
                .map((ans) => {
                  const isCorrect = correctIds.has(ans.id);
                  return (
                    <div
                      key={ans.id ?? `${ans.text}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 4,
                      }}
                    >
                      {isCorrect ? (
                        <CheckCircleTwoTone
                          twoToneColor="#52c41a"
                          style={{ marginRight: 6 }}
                        />
                      ) : (
                        <CloseCircleTwoTone
                          twoToneColor="#ff4d4f"
                          style={{ marginRight: 6 }}
                        />
                      )}
                      <span
                        style={{ color: isCorrect ? '#1a7f37' : undefined }}
                      >
                        {ans.text}
                      </span>
                    </div>
                  );
                })
            ) : (
              <div>{t('Žiadne odpovede')}</div>
            )}
          </div>
        </div>
      )}
    </StyledModal>
  );
};

QuizQuestionPreviewModal.propTypes = {
  open: PropTypes.bool,
  questionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
};

export default QuizQuestionPreviewModal;
